import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Person, Bell, ShieldKeyhole, DisplayPulse } from '@gravity-ui/icons';
import ProfileAccountTab from './tabs/ProfileAccountTab';
import ProfileNotificationsTab from './tabs/ProfileNotificationsTab';
import ProfileSecurityTab from './tabs/ProfileSecurityTab';
import ProfileControlTab from './tabs/ProfileControlTab';
import { getCurrentUser } from '../../services/auth';
import { describeHttpError } from '../../utils/apiError';
import { useNavigationEntitlements } from '../../hooks/useNavigationEntitlements';
import { isNavigationKeyAllowed } from '../../utils/navigationEntitlements';
import './ProfilePage.css';

/*
 * Личный кабинет (PART 3, раздел 7).
 * Спека: docs/superpowers/specs/2026-09-06-violations-part3-design.md
 *
 * Прежняя страница с деревом талантов сохранена в ProfilePageLegacy.js.
 */

// Иконки видны только на телефоне (см. ProfilePage.css, ≤767px): там вкладки
// стоят 4-в-ряд с иконкой сверху и подписью снизу, без горизонтального скролла.
export const TABS = [
  { id: 'account', label: 'Профиль', Icon: Person },
  { id: 'notifications', label: 'Уведомления', Icon: Bell },
  { id: 'security', label: 'Безопасность', Icon: ShieldKeyhole },
  // navKey — ключ «Видимых вкладок» (Роли): снятая там вкладка здесь скрыта.
  { id: 'control', label: 'Контроль', Icon: DisplayPulse, navKey: 'profile.control' },
];

const ProfilePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const { keys: navigationKeys } = useNavigationEntitlements();
  const shownTabs = TABS.filter((tab) => !tab.navKey || isNavigationKeyAllowed(navigationKeys, tab.navKey));
  const requestedTab = searchParams.get('tab');
  const activeTab = shownTabs.some((tab) => tab.id === requestedTab) ? requestedTab : 'account';

  useEffect(() => {
    let cancelled = false;
    getCurrentUser()
      .then((me) => { if (!cancelled) setUser(me); })
      .catch((requestError) => {
        if (!cancelled) setError(describeHttpError(requestError, 'Не удалось загрузить профиль'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Состояние вкладки (например, scope и период «Контроля») в URL другой вкладки
  // не переносится: иначе адрес обещал бы фильтры, которых на экране нет.
  const openTab = (tabId) => {
    setSearchParams(tabId === 'account' ? {} : { tab: tabId });
  };

  return (
    <div className="pp-container">
      <h1 className="pp-title">Личный кабинет</h1>

      <div className="pp-tabs" role="tablist">
        {shownTabs.map(({ id, label, Icon }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={activeTab === id}
            className={`pp-tab-btn${activeTab === id ? ' pp-tab-btn--active' : ''}`}
            onClick={() => openTab(id)}
          >
            <Icon className="pp-tab-icon" width={20} height={20} aria-hidden />
            <span className="pp-tab-label">{label}</span>
          </button>
        ))}
      </div>

      {activeTab === 'account' && <ProfileAccountTab user={user} loading={loading} error={error} />}
      {activeTab === 'notifications' && <ProfileNotificationsTab />}
      {activeTab === 'security' && <ProfileSecurityTab />}
      {activeTab === 'control' && <ProfileControlTab />}
    </div>
  );
};

export default ProfilePage;
